import type { ReactNode } from 'react'
import { Steps, Tag, Typography } from 'antd'
import type { QualityCfg } from './qualitySettingsTypes'

const { Text, Paragraph } = Typography

export const METHOD_GROUPS: { label: string; color: string; items: { label: string; score: number }[] }[] = [
  {
    label: '优质解析（80+ 分）',
    color: 'success',
    items: [
      { label: 'PDF 文字层', score: 95 },
      { label: 'Word (.docx)', score: 95 },
      { label: 'Excel (.xlsx)', score: 90 },
      { label: 'Word (.doc)', score: 85 },
      { label: 'Excel (.xls)', score: 85 },
      { label: 'PowerPoint', score: 80 },
    ],
  },
  {
    label: '一般解析（50–79 分）',
    color: 'processing',
    items: [
      { label: 'PDF 图文混合', score: 70 },
      { label: 'PDF 扫描 OCR', score: 55 },
      { label: '图片 OCR', score: 50 },
    ],
  },
  {
    label: '较差解析（<50 分）',
    color: 'error',
    items: [
      { label: '图片未 OCR', score: 15 },
      { label: '格式不支持', score: 5 },
      { label: '解析失败', score: 5 },
    ],
  },
]

export function pct(n: number, digits = 0) {
  return `${(n * 100).toFixed(digits)}%`
}

export function weightLabel(w: number | undefined, share?: number) {
  const v = w ?? 0
  if (share != null && share > 0) {
    return `权重 ${v.toFixed(2)}（合成规则分时约占 ${pct(share)}）`
  }
  return `权重 ${v.toFixed(2)}（相对比重，系统会自动归一化）`
}

function formatRuleScoreFormula(
  parts: { title: string; weight: number }[],
  weightSum: number,
) {
  if (!parts.length) return '请启用至少一个评分维度'
  const weighted = parts.map((p) => `${p.title}×${p.weight.toFixed(2)}`).join(' + ')
  if (parts.length === 1) return weighted
  return `(${weighted}) ÷ ${weightSum.toFixed(2)}`
}

const RULE_DIM_KEYS = ['extraction', 'structure', 'index_health'] as const

export function ruleDimensionWeightShare(
  dimensions: QualityCfg['dimensions'] | undefined,
  key: (typeof RULE_DIM_KEYS)[number],
) {
  let sum = 0
  for (const k of RULE_DIM_KEYS) {
    const d = dimensions?.[k]
    if (d?.enabled === false) continue
    const w = Number(d?.weight ?? 0)
    if (w > 0) sum += w
  }
  const d = dimensions?.[key]
  if (d?.enabled === false || sum <= 0) return undefined
  const w = Number(d?.weight ?? 0)
  return w > 0 ? w / sum : undefined
}

export function QualityFormulaSummary({
  dimensions,
  tiers,
  useLlm,
}: {
  dimensions: QualityCfg['dimensions'] | undefined
  tiers: QualityCfg['tiers'] | undefined
  useLlm: boolean
}) {
  const highMin = tiers?.high_min ?? 75
  const mediumMin = tiers?.medium_min ?? 45

  if (useLlm) {
    return (
      <div className="quality-rules-guide quality-rules-guide--compact">
        <Steps
          size="small"
          className="quality-flow-steps"
          items={[
            { title: '读文档', description: '标题+摘要' },
            { title: 'AI 打分', description: '0–100' },
            { title: '映射档位', description: '高/中/低' },
          ]}
        />
        <div className="quality-formula-box">
          <Paragraph type="secondary" className="quality-formula-extra" style={{ marginTop: 0 }}>
            <strong>AI 评分模式</strong>：对话模型阅读文档后直接给出 0–100 综合分；下方「评审标准」会写入 AI 提示词。
            不运行程序规则维度。
          </Paragraph>
        </div>
        <div className="quality-tier-tags">
          <Tag color="success">高 ≥ {highMin}</Tag>
          <Tag color="processing">中 ≥ {mediumMin}</Tag>
          <Tag color="default">低 &lt; {mediumMin}</Tag>
          <Tag color="error">一票否决 → 低</Tag>
        </div>
      </div>
    )
  }

  const ext = dimensions?.extraction
  const struct = dimensions?.structure
  const idx = dimensions?.index_health

  const ruleParts = [
    { title: '正文是否可读', weight: ext?.weight ?? 0.3, enabled: ext?.enabled !== false },
    { title: '篇幅是否充实', weight: struct?.weight ?? 0.25, enabled: struct?.enabled !== false },
    { title: '索引是否正常', weight: idx?.weight ?? 0.2, enabled: idx?.enabled !== false },
  ].filter((p) => p.enabled && p.weight > 0)

  const weightSum = ruleParts.reduce((s, p) => s + p.weight, 0)
  const formula = formatRuleScoreFormula(ruleParts, weightSum)
  const shareText =
    weightSum > 0
      ? ruleParts.map((p) => `${p.title} ${pct(p.weight / weightSum)}`).join(' · ')
      : ''

  return (
    <div className="quality-rules-guide quality-rules-guide--compact">
      <Steps
        size="small"
        className="quality-flow-steps"
        items={[
          { title: '维度打分', description: '0–100' },
          { title: '加权平均', description: '规则分' },
          { title: '映射档位', description: '高/中/低' },
        ]}
      />
      <div className="quality-formula-box">
        <Text type="secondary">规则分 = </Text>
        <Text code>{formula}</Text>
        {shareText ? (
          <Paragraph type="secondary" className="quality-formula-extra">
            <strong>程序规则模式</strong>：权重为相对比重，不必凑满 1.0；系统除以权重之和做加权平均。当前占比：{shareText}
          </Paragraph>
        ) : null}
      </div>
      <div className="quality-tier-tags">
        <Tag color="success">高 ≥ {highMin}</Tag>
        <Tag color="processing">中 ≥ {mediumMin}</Tag>
        <Tag color="default">低 &lt; {mediumMin}</Tag>
        <Tag color="error">一票否决 → 低</Tag>
      </div>
    </div>
  )
}

export function QualityRuleAside({ children }: { children: ReactNode }) {
  return <div className="quality-rule-aside">{children}</div>
}

export function ExtractionRules({ dimensions }: { dimensions: QualityCfg['dimensions'] | undefined }) {
  const ext = dimensions?.extraction
  return (
    <>
      <ul className="quality-rules-list">
        <li>按解析方式给基础分（越高越可靠）</li>
        <li>
          有效字数 &lt; {ext?.min_chars ?? 200} → 最高 <strong>35</strong> 分
        </li>
        <li>
          乱码 &gt; {pct(ext?.garble_max ?? 0.3)} → 最高 <strong>40</strong> 分
        </li>
        <li>有标题结构 → 基础分 +5</li>
      </ul>
      <div className="quality-method-groups">
        {METHOD_GROUPS.map((g) => (
          <div key={g.label} className="quality-method-group">
            <Text type="secondary" className="quality-method-group-label">
              {g.label}
            </Text>
            <div className="quality-method-tags">
              {g.items.map((item) => (
                <Tag key={item.label} color={g.color}>
                  {item.label} {item.score}
                </Tag>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export function StructureRules({ dimensions }: { dimensions: QualityCfg['dimensions'] | undefined }) {
  const struct = dimensions?.structure
  return (
    <ul className="quality-rules-list">
      <li>
        ≥ {struct?.rich_chars ?? 2000} 字 → <strong>95</strong> 分
      </li>
      <li>
        ≥ {struct?.thin_chars ?? 500} 字 → <strong>70</strong> 分
      </li>
      <li>
        &lt; {struct?.thin_chars ?? 500} 字 → <strong>45</strong> 分
      </li>
    </ul>
  )
}

export function IndexHealthRules() {
  return (
    <ul className="quality-rules-list">
      <li>索引失败 → <strong>0</strong></li>
      <li>无切片 → <strong>10</strong></li>
      <li>1 片 → <strong>65</strong> · 2–4 片 → <strong>80</strong> · ≥5 片 → <strong>92</strong></li>
    </ul>
  )
}

export function AiScoringRules({ tiers }: { tiers: QualityCfg['tiers'] | undefined }) {
  const highMin = tiers?.high_min ?? 75
  const mediumMin = tiers?.medium_min ?? 45
  return (
    <ul className="quality-rules-list">
      <li>AI 阅读标题 + 正文前 2500 字，输出 0–100 综合分</li>
      <li>左侧「评审标准」与右侧分档阈值会一并写入 AI 提示词</li>
      <li>
        综合分 ≥ {highMin} → 高；≥ {mediumMin} → 中；否则低
      </li>
      <li>需已在「模型管理」配置对话模型 API</li>
    </ul>
  )
}

export function RedundancyRules({ redundancy }: { redundancy: QualityCfg['redundancy'] | undefined }) {
  const lightMin = redundancy?.light_min ?? 0.2
  const heavyMin = redundancy?.heavy_min ?? 0.6
  const sampleLimit = redundancy?.sample_limit ?? 30
  return (
    <ul className="quality-rules-list">
      <li>与同库最多 {sampleLimit} 篇文档比对连续相同文字</li>
      <li>重复占比 = 相同字数 ÷ 本文总字数</li>
      <li>
        ≥ {pct(lightMin)} 轻度 · ≥ {pct(heavyMin)} 高度（连续相同 ≥13 字，见查重设置）
      </li>
    </ul>
  )
}

export function TierRules({ tiers }: { tiers: QualityCfg['tiers'] | undefined }) {
  const highMin = tiers?.high_min ?? 75
  const mediumMin = tiers?.medium_min ?? 45
  return (
    <ul className="quality-rules-list">
      <li>
        综合分 ≥ {highMin} → <strong>高</strong>
      </li>
      <li>
        综合分 ≥ {mediumMin} 且未达高 → <strong>中</strong>
      </li>
      <li>
        其余 → <strong>低</strong>；一票否决项直接标低
      </li>
    </ul>
  )
}
